import React, { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { Localized } from "../generated/content";

export type Language = "tr" | "en";

interface LanguageContextValue {
  lang: Language;
  setLang: (lang: Language) => void;
  /** Pick the current language out of a localized field. */
  t: (value: Localized | undefined) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = "language";

function readStoredLanguage(): Language {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "en" ? "en" : "tr";
  } catch {
    // Private browsing modes can throw on localStorage access.
    return "tr";
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(readStoredLanguage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      // Preference simply is not remembered; not worth failing over.
    }
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<LanguageContextValue>(
    () => ({
      lang,
      setLang,
      // Falls back to Turkish when a translation is missing so English
      // visitors get real content instead of an empty block.
      t: (value) => (value ? value[lang] || value.tr || "" : ""),
    }),
    [lang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used inside a LanguageProvider");
  }
  return context;
}
